/**
 * Verknuepfungen zwischen Bereich, Ziel, Projekt und Aufgabe (Roadmap F-11).
 *
 * Jede Aenderung an einer Verknuepfung schreibt zuerst ihren eigenen Teil und
 * wendet danach, in derselben Transaktion, die EINE Regel aus
 * `domain/links/hierarchy.ts` auf den ganzen Bestand an. Es gibt also keine
 * zweite, handgeschriebene Fassung der Kaskade ("Ziel wechselt den Bereich ->
 * Projekte -> deren Aufgaben"), die irgendwann von der Regel abweichen
 * koennte. Bei den Datenmengen eines einzelnen Menschen kostet das
 * Millisekunden.
 */
import { db, type OlePrivatDB } from '@/db/schema';
import { reconcileLinks } from '@/domain/links/hierarchy';
import { nowInstant } from '@/lib/datetime';
import type { ID } from '@/domain/types';

/**
 * Die Regel auf den gesamten Bestand anwenden und nur Geaendertes schreiben.
 *
 * Laeuft als Teil der Transaktion des Aufrufers, wenn es eine gibt. Die
 * Datenbank ist waehlbar, damit auch das Wiederherstellen aus dem Papierkorb
 * (F-45, mit zwei Geraeten getestet) dieselbe Regel nutzt.
 */
export async function applyLinkRule(datenbank: OlePrivatDB = db): Promise<void> {
  const d = datenbank;
  await d.transaction('rw', d.areas, d.goals, d.projects, d.tasks, async () => {
    const [areas, goals, projects, tasks] = await Promise.all([
      d.areas.toArray(), d.goals.toArray(), d.projects.toArray(), d.tasks.toArray(),
    ]);
    const r = reconcileLinks({ areaIds: areas.map((a) => a.id), goals, projects, tasks });
    const ts = nowInstant();
    const geaendert = <T extends { id: ID }>(rows: T[], ids: Set<ID>) =>
      rows.filter((x) => ids.has(x.id)).map((x) => ({ ...x, updatedAt: ts }));

    await Promise.all([
      d.goals.bulkPut(geaendert(r.goals, r.changed.goals)),
      d.projects.bulkPut(geaendert(r.projects, r.changed.projects)),
      d.tasks.bulkPut(geaendert(r.tasks, r.changed.tasks)),
    ]);
  });
}

/** Projekt einem Ziel zuordnen (`null` loest es). Der Bereich folgt dem Ziel. */
export async function setProjectGoal(projectId: ID, goalId: ID | null): Promise<void> {
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.projects.where('id').equals(projectId).modify((p) => {
      if (goalId === null) delete p.goalId;
      else p.goalId = goalId;
      p.updatedAt = nowInstant();
    });
    await applyLinkRule();
  });
}

/**
 * Aufgabe einem Projekt zuordnen (`null` loest sie).
 *
 * Ein direkter Zielverweis entfaellt dabei: der Weg nach oben fuehrt dann
 * ueber das Projekt. Beim Loesen behaelt die Aufgabe ihren Bereich - sie
 * verschwindet so nicht ploetzlich aus einer gefilterten Ansicht.
 */
export async function setTaskProject(taskId: ID, projectId: ID | null): Promise<void> {
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.tasks.where('id').equals(taskId).modify((t) => {
      if (projectId === null) {
        delete t.projectId;
      } else {
        t.projectId = projectId;
        delete t.goalId;
      }
      t.updatedAt = nowInstant();
    });
    await applyLinkRule();
  });
}

/** Aufgabe direkt einem Ziel zuordnen (`null` loest sie). Ein Projekt entfaellt dabei. */
export async function setTaskGoal(taskId: ID, goalId: ID | null): Promise<void> {
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.tasks.where('id').equals(taskId).modify((t) => {
      if (goalId === null) {
        delete t.goalId;
      } else {
        t.goalId = goalId;
        delete t.projectId;
      }
      t.updatedAt = nowInstant();
    });
    await applyLinkRule();
  });
}

/**
 * Bereich eines Ziels setzen. Seine Projekte und deren Aufgaben ziehen mit -
 * sonst stuenden sie nach einem Bereichswechsel im alten Bereich.
 */
export async function setGoalArea(goalId: ID, areaId: ID | null): Promise<void> {
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.goals.update(goalId, { areaId, updatedAt: nowInstant() });
    await applyLinkRule();
  });
}

/**
 * Alle Verknuepfungen einer Aufgabe auf einmal setzen - fuer den
 * Aufgaben-Dialog, der Projekt, Ziel und Bereich gemeinsam speichert.
 *
 * Projekt schlaegt Ziel: ist beides angegeben, gilt das Projekt. Der Bereich
 * zaehlt nur ohne Elternteil; sonst setzt ihn die Regel auf den des
 * Elternteils.
 */
export async function setTaskLinks(
  taskId: ID,
  links: { projectId: ID | null; goalId: ID | null; areaId: ID | null },
): Promise<void> {
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.tasks.where('id').equals(taskId).modify((t) => {
      if (links.projectId !== null) t.projectId = links.projectId;
      else delete t.projectId;
      if (links.projectId === null && links.goalId !== null) t.goalId = links.goalId;
      else delete t.goalId;
      t.areaId = links.areaId;
      t.updatedAt = nowInstant();
    });
    await applyLinkRule();
  });
}
